import React, {
  useState,
  useImperativeHandle,
  forwardRef,
  useEffect,
  useRef,
  useMemo,
} from "react";
import { DynamicForm } from "@shared/components/DynamicForm";
import { FormField, FormService } from "@shared/services/FormServices";
import {
  useGetAreas,
  useGetCertificates,
  useGetSkills,
  useGetSubAreas,
  useUpdateOtherDetails,
} from "@features/wx/fieldForce/hooks";
import { useRates } from "@features/wx/settings/hooks";
import { useNavigate, useParams } from "react-router-dom";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import { logEvent } from "@shared/analytics/analytics";

type PersonData = {
  id: string;
  name: string;
  role: string;
  zone: string;
  area: string;
  contact: string;
  joiningDate: string;
  status: string;
  email?: string;
  imei?: string;
  address?: string;
};

type OtherDetailsFormData = {
  skills: string[];
  area: string[];
  subArea: string[];
  normalPay: string;
  overtimePay: string;
  certificateName: string[];
};

interface EditOtherDetailsTabProps {
  initialPersonData?: any | null;
  currentFormData?: OtherDetailsFormData | null;
  onFormDataChange?: (data: OtherDetailsFormData | null) => void;
}

export const EditOtherDetailsTab: React.FC<EditOtherDetailsTabProps> = ({
  initialPersonData,
  currentFormData,
  onFormDataChange,
}) => {
  const { id, ff_no } = useParams<{ id: string; ff_no: string }>();
  const remoteUtilityId = useRemoteUtilityId();
  const navigate = useNavigate();
  const [codeList, setCodeList] = useState<any>("");
  const { data: skills } = useGetSkills();
  const { data: certificates } = useGetCertificates();
  const { data: areas } = useGetAreas();
  const { data: subAreas } = useGetSubAreas(codeList);
  const { data: rates } = useRates();
  const updateOtherDetails = useUpdateOtherDetails(ff_no);

  const findRateIdByValue = (
    rateValue: number,
    rateType: "normal" | "overtime"
  ): string => {
    if (!rates || !Array.isArray(rates) || !rateValue) return "";

    const rateObj = rates.find((rate) => {
      if (rate.type !== rateType) return false;

      // Extract numeric value from rate string
      const rateStr = rate.rate;
      if (typeof rateStr === "string") {
        const match = rateStr.match(/\d+/);
        const extractedValue = match ? parseInt(match[0], 10) : null;
        return extractedValue === rateValue;
      }

      return typeof rateStr === "number" && rateStr === rateValue;
    });

    return rateObj ? rateObj.id.toString() : "";
  };

  const getInitialFormData = (): OtherDetailsFormData => {
    console.log("initialPersonData:", initialPersonData);
    console.log("currentFormData:", currentFormData);

    if (currentFormData) return currentFormData;

    // Convert numeric rate values back to rate IDs for the form
    const normalPayId = initialPersonData?.additionalData?.normalPay
      ? findRateIdByValue(initialPersonData.additionalData.normalPay, "normal")
      : "";

    const overtimePayId = initialPersonData?.additionalData?.overtimePay
      ? findRateIdByValue(
          initialPersonData.additionalData.overtimePay,
          "overtime"
        )
      : "";

    const result = {
      skills: initialPersonData?.skills || [],
      area: initialPersonData?.area || [],
      subArea: initialPersonData?.subArea || [],
      normalPay: normalPayId,
      overtimePay: overtimePayId,
      certificateName: initialPersonData?.additionalData?.certificates || [],
    };

    console.log("getInitialFormData result:", result);
    console.log("normalPayId:", normalPayId, "overtimePayId:", overtimePayId);
    return result;
  };

  const [formData, setFormData] =
    useState<OtherDetailsFormData>(getInitialFormData);
  const isInternalUpdate = useRef(false);
  const hasInitialized = useRef(false);

  console.log("Areas data:", areas);
  console.log("SubAreas data:", subAreas);
  console.log("CodeList:", codeList);

  const skillOptions = useMemo(() => {
    if (!skills || !Array.isArray(skills)) {
      return [];
    }
    return skills.map((data) => ({
      value: data.id,
      label: data.skill,
    }));
  }, [skills]);

  const certificateOptions = useMemo(() => {
    if (!certificates || !Array.isArray(certificates)) {
      return [];
    }
    return certificates.map((data) => ({
      value: data.code,
      label: data.name,
    }));
  }, [certificates]);

  const areaOptions = useMemo(() => {
    if (!areas || !Array.isArray(areas)) {
      return [];
    }
    return areas.map((data) => ({
      value: data.code,
      label: data.name,
    }));
  }, [areas]);

  const subAreaOptions = useMemo(() => {
    if (!subAreas || !Array.isArray(subAreas)) {
      return [];
    }
    return subAreas.map((data) => ({
      value: data.code,
      label: data.name,
    }));
  }, [subAreas]);

  // Filter rates by type and create options
  const normalRateOptions = useMemo(() => {
    if (!rates || !Array.isArray(rates)) {
      return [];
    }
    return rates
      .filter((rate) => rate.type === "normal" && rate.isActive === true)
      .map((rate) => ({
        value: rate.id.toString(),
        label: `${rate.name} - ${rate.rate}`,
      }));
  }, [rates]);

  const overtimeRateOptions = useMemo(() => {
    if (!rates || !Array.isArray(rates)) {
      return [];
    }
    return rates
      .filter((rate) => rate.type === "overtime" && rate.isActive === true)
      .map((rate) => ({
        value: rate.id.toString(),
        label: `${rate.name} - ${rate.rate}`,
      }));
  }, [rates]);

  const formFields: FormField[] = [
    {
      name: "skills",
      label: "Skills",
      type: "multi-select",
      required: false,
      options: skillOptions,
      placeholder: "Select skills...",
      fullWidth: true,
      searchable: true,
      clearable: true,
      maxDisplayItems: 3,
      closeOnSelect: false,
      className: "col-span-1",
      classes: {
        container: "w-full mb-4",
        label: "block text-sm font-medium text-gray-700 mb-2",
        select:
          "w-full border-2 border-blue-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200",
        error: "text-red-600 text-sm mt-1",
      },
    },
    {
      name: "certificateName",
      label: "Certifications",
      type: "multi-select",
      required: false,
      options: certificateOptions,
      placeholder: "Select certifications...",
      fullWidth: true,
      searchable: true,
      clearable: true,
      maxDisplayItems: 3,
      closeOnSelect: false,
      className: "col-span-1",
      classes: {
        container: "w-full mb-4",
        label: "block text-sm font-medium text-gray-700 mb-2",
        select:
          "w-full border-2 border-blue-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200",
        error: "text-red-600 text-sm mt-1",
      },
    },
    {
      name: "area",
      type: "multi-select",
      label: "Area",
      placeholder: "Select area",
      required: false,
      options: areaOptions,
      className: "col-span-1",
    },
    {
      name: "subArea",
      type: "multi-select",
      label: "Sub Area",
      placeholder: "Select sub area",
      options: subAreaOptions,
      required: false,
      className: "col-span-1",
    },
    {
      name: "normalPay",
      type: "select",
      label: "Normal Pay Rate (SAT/Hr)",
      placeholder: "Select normal rate",
      required: false,
      options: normalRateOptions,
      className: "col-span-1",
      searchable: true,
      clearable: true,
    },
    {
      name: "overtimePay",
      type: "select",
      label: "Overtime Pay Rate (SAT/Hr)",
      placeholder: "Select overtime rate",
      required: false,
      options: overtimeRateOptions,
      className: "col-span-1",
      searchable: true,
      clearable: true,
    },
  ];

  const form = FormService.createForm({
    fields: formFields,
    mode: "onChange",
    validateOnMount: false,
    defaultValues: formData,
  });

  // FIXED: Main initialization effect
  useEffect(() => {
    const dataToUse = currentFormData || getInitialFormData();

    console.log("useEffect - dataToUse:", dataToUse);
    console.log("useEffect - dataToUse.area:", dataToUse.area);
    console.log("useEffect - dataToUse.area.length:", dataToUse.area?.length);

    if (!hasInitialized.current || currentFormData) {
      isInternalUpdate.current = true;
      setFormData(dataToUse);

      // FIXED: Reset form with a small delay to ensure form is ready
      setTimeout(() => {
        form.reset(dataToUse);
        console.log("Form reset completed with data:", dataToUse);
      }, 0);

      hasInitialized.current = true;

      // Set codeList if area data exists
      if (dataToUse.area && dataToUse.area.length > 0) {
        console.log("Setting codeList to:", dataToUse.area);
        setCodeList(dataToUse.area);
      } else {
        console.log("NOT setting codeList - area is empty or undefined");
      }

      isInternalUpdate.current = false;
    }
  }, [initialPersonData, currentFormData, form]);

  // ADDED: Separate effect to handle when initialPersonData loads from undefined to actual data
  useEffect(() => {
    if (
      initialPersonData &&
      initialPersonData.area &&
      initialPersonData.area.length > 0 &&
      !codeList
    ) {
      console.log(
        "Setting codeList from initialPersonData change:",
        initialPersonData.area
      );
      setCodeList(initialPersonData.area);
    }
  }, [initialPersonData, codeList]);

  // ADDED: Effect to ensure form gets updated when both data and options are ready
  useEffect(() => {
    if (
      initialPersonData &&
      areas &&
      formData.area.length === 0 &&
      initialPersonData.area &&
      initialPersonData.area.length > 0
    ) {
      console.log("Updating form with initial data when options are ready");
      const updatedFormData = getInitialFormData();
      isInternalUpdate.current = true;
      setFormData(updatedFormData);
      setTimeout(() => {
        form.reset(updatedFormData);
        console.log("Late form reset with:", updatedFormData);
      }, 100);
      isInternalUpdate.current = false;
    }
  }, [initialPersonData, areas, formData.area.length, form]);

  // FIXED: Handle currentFormData changes separately
  useEffect(() => {
    if (currentFormData && hasInitialized.current) {
      isInternalUpdate.current = true;
      setFormData(currentFormData);
      form.reset(currentFormData);

      // Set codeList if area data exists
      if (currentFormData.area && currentFormData.area.length > 0) {
        console.log(
          "Setting codeList from currentFormData change:",
          currentFormData.area
        );
        setCodeList(currentFormData.area);
      }
      isInternalUpdate.current = false;
    }
  }, [currentFormData, form]);

  const handleFieldChange = (fieldName: string, value: any) => {
    if (isInternalUpdate.current) return;

    const newFormData = {
      ...formData,
      [fieldName]: value,
    };

    setFormData(newFormData);

    // Only call onFormDataChange if it exists
    if (onFormDataChange) {
      onFormDataChange(newFormData);
    }

    switch (fieldName) {
      case "area":
        console.log("Area changed to:", value);
        setCodeList(value);
        break;
    }
  };

  const handleSubmit = (data: any) => {
    // Helper function to extract numeric value from rate
    const extractRateValue = (
      rateId: string,
      rateType: "normal" | "overtime"
    ) => {
      if (!rateId || !rates) return null;

      const rateObj = rates.find(
        (rate) => rate.id.toString() === rateId && rate.type === rateType
      );

      if (!rateObj) return null;

      // Extract numeric value from rate string
      const rateStr = rateObj.rate;
      if (typeof rateStr === "string") {
        // Handle formats like "$300/hr", "$300", "300", etc.
        const match = rateStr.match(/\d+/);
        return match ? parseInt(match[0], 10) : null;
      }

      // If rate is already a number
      return typeof rateStr === "number" ? rateStr : null;
    };

    const normalPayRate = extractRateValue(data?.normalPay, "normal");
    const overtimePayRate = extractRateValue(data?.overtimePay, "overtime");

    const payload = {
      ff_no: ff_no,
      skills: data.skills || [],
      area: data.area || [],
      subArea: data.subArea || [],
      remote_utility_id: remoteUtilityId,
      status: 0,
      is_field_force_info: true,
      normal_pay: normalPayRate,
      overtime_pay: overtimePayRate,
      certificates: data?.certificateName || [],
    };

    console.log("Payload with extracted rates:", payload);
    updateOtherDetails.mutateAsync(payload, {
      onSuccess: () => {
        logEvent(`Field Force - ${ff_no} Updated`, {
          ...payload
        })
      }
    });

    // Clear saved form data on successful submit
    if (onFormDataChange) {
      onFormDataChange(null);
    }

    navigate(
      `/service-orders/field-force/edit/${id}/${ff_no}?tab=availability`,
      { replace: true }
    );
  };

  const handleReset = () => {
    // Clear saved form data on reset
    if (onFormDataChange) {
      onFormDataChange(null);
    }
    navigate(`/service-orders/field-force/`);
  };

  // Debug logs to monitor the state
  console.log("Current state:", {
    hasInitialized: hasInitialized.current,
    codeList,
    formDataArea: formData.area,
    initialPersonDataExists: !!initialPersonData,
    areasExists: !!areas,
    subAreasExists: !!subAreas,
  });

  // Show loading state while data is loading
  if (!initialPersonData || !areas) {
    return (
      <div className="space-y-6 p-2">
        <div className="flex justify-center items-center py-8">
          <div className="text-gray-500">Loading form data...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-2">
      {/* Dynamic Form with Grid Layout */}
      <DynamicForm
        fields={formFields}
        form={form}
        onSubmit={handleSubmit}
        onReset={handleReset}
        onFieldChange={handleFieldChange}
        gridColumns={2}
        responsiveColumns={{
          sm: 1,
          md: 2,
        }}
        config={{
          showResetButton: true,
          showSubmitButton: true,
          submitButtonText: "Update Technician",
          resetButtonText: "Cancel",
          resetOnSubmit: false,
          spacing: "comfortable",
          classes: {
            form: "space-y-8",
            fieldGroup:
              "bg-white border border-gray-200 rounded-lg p-6 shadow-sm",
            button: "px-6 py-2 rounded-md font-medium transition-colors",
            submitButton:
              "bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50",
            resetButton:
              "border border-gray-300 text-gray-700 bg-white hover:bg-gray-50",
            buttonContainer:
              "flex justify-end gap-3 pt-6 border-t border-gray-200 mt-8",
            input:
              "w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500",
            textarea:
              "w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-vertical",
            select:
              "w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white",
            label: "block text-sm font-medium text-gray-700 mb-1",
            error: "text-sm text-red-600 mt-1",
            helperText: "text-sm text-gray-500 mt-1 flex",
            checkbox:
              "h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500",
            container: "space-y-1",
          },
        }}
      />
    </div>
  );
};
